import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Typed capture, the web's half of what TextEntryView does on iOS and TextEntryViewModel on
 * Android: type the idea, optionally let Gemini structure it, review, save.
 */

/** Mirrors `MAX_TRANSCRIPT_LENGTH` in processTranscript.ts; longer input is rejected there. */
export const MAX_CAPTURE_LENGTH = 20000;

/** What `processTranscript` returns. `recallHints` is present only when it found some. */
export interface Extraction {
  title: string;
  description: string;
  tags: string[];
  links: string[];
  recallHints?: Record<string, unknown>;
}

const callProcessTranscript = httpsCallable<{ transcript: string }, Extraction>(
  functions,
  'processTranscript'
);

/**
 * Send typed text through the same Gemini extraction the phones' voice and text capture use.
 *
 * The function sanitises links to http(s) and whitelists recall hints server-side, so what
 * comes back is safe to store as-is. It is billable per call, which is why this runs only
 * when asked for — never on a keystroke.
 */
export async function extractIdea(text: string): Promise<Extraction> {
  const { data } = await callProcessTranscript({ transcript: text });
  return {
    title: data.title ?? '',
    description: data.description ?? '',
    tags: Array.isArray(data.tags) ? data.tags : [],
    links: Array.isArray(data.links) ? data.links : [],
    ...(data.recallHints ? { recallHints: data.recallHints } : {}),
  };
}

/** iOS uses the first eight words as an unprocessed note's title; Android agrees. */
const PLAIN_TITLE_WORDS = 8;

/**
 * The title a note saved without AI gets: its first eight words.
 *
 * Matches `saveAsPlainNote()` on iOS and `NoteComposer.plainNoteTitle` on Android, so all
 * three clients name an unprocessed note identically.
 */
export function plainNoteTitle(text: string): string {
  return text.trim().split(/\s+/).filter(Boolean).slice(0, PLAIN_TITLE_WORDS).join(' ');
}
